import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { clone, filter, isEmpty, omit } from 'lodash-es';

import { CampaignType } from '@dev/ee-api/ee-api-models';
import { MultiAutocomplete, MultiAutocompleteOptionProps } from '@dev/ee-components';
import { safelyDecodeBase64Url } from '@dev/ee-tools';

import { ErrorSubscriberOrder } from '@/components/CampaignCreator/CampaignCreatorInterface';
import { isAllContactsOptionSelected } from '@/components/CampaignCreator/helpers';
import { useCampaignScreensPathsChecker } from '@/components/CampaignCreator/hooks/useCampaignScreensPathsChecker';

import { getSegmentsStore } from '@/store/segments/actions';
import { getListstStore } from '@/store/lists/actions';
import {
    CountRecipientsStart,
    getCampainCreatorStore,
    ModifyCampaignMap,
    SetCampaignErrors,
    SetCampaignErrorsSubscriber,
    SetDefaultTarget,
} from '@/store/campaignCreator/actions';
import { CampaignErrorNames } from '@/store/campaignCreator/types';
import { useAutocompleteOptions } from '../../hooks/useAutocompleteOptions';
import { OptionProps } from '../../SendToInterface';
import { convertToCampaignRecipient } from '../../utils/convertToCampaignRecipient';

const ALL_CONTACTS_SEGMENT = 'All Contacts';

const id = 'campaign-creator-send-to-input';
const targetsErrorName = CampaignErrorNames.TARGETS;
const noTargetsSelectedErrorName = CampaignErrorNames.NO_TARGETS_SELECTED;
const noRecipientsErrorName = CampaignErrorNames.NO_RECIPIENTS;
const notEnoughRecipientsErrorName = CampaignErrorNames.NOT_ENOUGH_RECIPIENTS;
const outOfRecipientsLimitErrorName = CampaignErrorNames.OUT_OF_RECIPIENTS_LIMIT;

export const Autocomplete = (): JSX.Element => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { listNamesCollection, isListNamesLoading } = useSelector(getListstStore);
    const { segmentNamesCollection, isLoadingSegmentsNames } = useSelector(getSegmentsStore);
    const { isNew, campaign, isLoading, errors } = useSelector(getCampainCreatorStore);
    const [chosenOptions, setChosenOptions] = useState<OptionProps[]>([]);
    const urlParams: { listname: string } = useParams();
    const isAutomated = campaign?.Type === CampaignType.Automated;

    const isError = !!errors?.[noTargetsSelectedErrorName];
    const hasAnyError =
        errors?.[targetsErrorName] ||
        errors?.[noTargetsSelectedErrorName] ||
        errors?.[noRecipientsErrorName] ||
        errors?.[notEnoughRecipientsErrorName] ||
        errors?.[outOfRecipientsLimitErrorName];
    const { isDetailsScreen } = useCampaignScreensPathsChecker();
    const options = useAutocompleteOptions(listNamesCollection, segmentNamesCollection, chosenOptions);
    const isDataLoaded = !isLoading && !isListNamesLoading && !isLoadingSegmentsNames && !isEmpty(campaign);

    useEffect((): void => {
        dispatch(
            SetCampaignErrorsSubscriber({
                id,
                errorName: targetsErrorName,
                order: ErrorSubscriberOrder.TARGETS,
            })
        );
    }, []);

    useEffect((): void => {
        if (!isDataLoaded) {
            return;
        }

        if (urlParams?.listname) {
            const listname = safelyDecodeBase64Url(urlParams?.listname);
            const option = options.filter((option) => option.label === listname);
            setChosenOptions(option);
            const values = convertToCampaignRecipient(option);
            dispatch(SetDefaultTarget(values));
            const allContactsOptionSelected = isAllContactsOptionSelected(option);
            !isDetailsScreen && !isAutomated && dispatch(CountRecipientsStart(allContactsOptionSelected));
            return;
        }
        if (!isNew && campaign) {
            const includesAllContacts = campaign?.Targets?.SegmentNames.includes(ALL_CONTACTS_SEGMENT);
            const option = options.filter((option) => {
                if (includesAllContacts && option.isAllContacts) {
                    return option;
                }
                return (
                    campaign.Targets.ListNames.includes(option.label) ||
                    campaign.Targets.SegmentNames.includes(option.label)
                );
            });
            setChosenOptions(option);
            const allContactsOptionSelected = isAllContactsOptionSelected(option);
            !isDetailsScreen && !isAutomated && dispatch(CountRecipientsStart(allContactsOptionSelected));
        }
    }, [isDataLoaded]);

    const getOptionsWithoutSelected = (options: MultiAutocompleteOptionProps[]): MultiAutocompleteOptionProps[] => {
        if (isEmpty(chosenOptions)) {
            return options;
        }

        let cloneOptions = [...options];

        for (let i = 0; i < chosenOptions.length; i++) {
            cloneOptions = filter(cloneOptions, (item) => item.label !== chosenOptions[i].label);
        }

        return cloneOptions;
    };

    const handleOnChange = (selectedOptions: OptionProps[]) => {
        setChosenOptions(selectedOptions);
        const allContactsOptionSelected = isAllContactsOptionSelected(selectedOptions);
        const values = convertToCampaignRecipient(selectedOptions);
        const modifyMap = new Map<string, any>();
        modifyMap.set('Targets', values);
        dispatch(ModifyCampaignMap(modifyMap));

        !isDetailsScreen && !isAutomated && dispatch(CountRecipientsStart(allContactsOptionSelected));
        if (hasAnyError) {
            const errorsObject = clone(errors) || {};
            const errorsToOmit = [
                targetsErrorName,
                noTargetsSelectedErrorName,
                noRecipientsErrorName,
                outOfRecipientsLimitErrorName,
                notEnoughRecipientsErrorName,
            ];
            const newErrorsObject = omit(errorsObject, errorsToOmit);
            dispatch(SetCampaignErrors(newErrorsObject));
        }
    };

    return (
        <MultiAutocomplete
            id={id}
            values={chosenOptions}
            options={getOptionsWithoutSelected(options)}
            onChange={handleOnChange}
            noOptionsText={t('noresults')}
            placeholder={t('searchorselectfromlist')}
            error={isError && isEmpty(chosenOptions)}
            readOnly={isDetailsScreen}
            inputProps={{
                autoFocus: isEmpty(errors),
            }}
            labelOnly={true}
        />
    );
};
